import React from 'react';
import { t } from 'i18next';
import {
  hasAnyPermissionForCompany,
  hasPermission,
  userIs,
} from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import { SimpleTextFilter } from '../../common/Table';
import { Link } from 'react-router-dom';
import ImageWithFallback from '../../common/ImageWithFallback';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import icons from '../../../constants/icons';
import { Sparklines, SparklinesCurve } from 'react-sparklines';
import AutoSelectText from '../../common/AutoSelectText';
import NoAppId from '../../common/NoAppId';
import CompanyLink from '../../common/links/CompanyLink';
import NoPackageId from '../../common/NoPackageId';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import NoProductCode from '../../common/NoProductCode';
import SkuRestrictions from '../../sku/components/SkuRestrictions';
import TerritoryLink from '../../common/links/TerritoryLink';
import PlatformLink from '../../common/links/PlatformLink';
import Checkbox from '../../common/Checkbox';
import KebabMenu from '../../common/KebabMenu';
import userTypes from '../../../constants/userTypes';
import Button from '../../common/Button';

export interface GamesColumnDefinitionsParams {
  currentUser: any;
  params: URLSearchParams;
  filterChange: (key: string, value: string) => void;
  games: any;
  onRequestKeys: (e: any, item: any) => void;
  onSteamBulkRequest: (e: any, items: any[]) => void;
  handleCheckChanged: (checked: boolean, item: any) => void;
  handleCheckAllChanged: (checked: boolean) => void;
  isChecked: (item: any) => boolean;
}

export const gamesColumnDefinitions = ({
  currentUser,
  params,
  filterChange,
  games,
  onRequestKeys,
  onSteamBulkRequest,
  handleCheckChanged,
  handleCheckAllChanged,
  isChecked,
}: GamesColumnDefinitionsParams) => [
    {
      Key: 'skuName',
      Label: t('SKU'),
      className: 'nowrap',
      Render: (item: any) =>
        hasAnyPermissionForCompany(
          currentUser,
          [permissionTypes.MANAGE_GAMES, permissionTypes.MANAGE_KEYS],
          item.publisherCompanyId
        ) ? (
          <Link to={`/sku/${item.skuId}`}>{item.skuName}</Link>
        ) : (
          item.skuName
        ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('name...')} testId={`${col.Key}-filter-input`} />
        ),
      },
    },
    {
      Key: 'gameName',
      Label: t('Game name'),
      className: 'flush',
      Render: (game: any) => (
        <div className="game-img-wrapper">
          <ImageWithFallback
            url={game.packshotUrl}
            fallBackImage={
              <div className="game-img">
                <div className="icon-wrapper">
                  <FontAwesomeIcon icon={icons.GAME} className={`game`} />
                </div>
              </div>
            }
            alt={game.name}
          ></ImageWithFallback>
          {hasPermission(currentUser, permissionTypes.MANAGE_GAMES) ? (
            <Link to={'/game/' + game.gameId + '?f_isArchived=Live'}>
              {game.gameName}
            </Link>
          ) : (
            <Link to={'/game/' + game.gameId}>{game.gameName}</Link>
          )}
        </div>
      ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} testId={`${col.Key}-filter-input`} />
        ),
      },
    },
    {
      Key: 'skuCount',
      Label: t('SKU Count'),
      className: 'text-right num no-wrap',
      Render: (game: any) => CommaNumber_NoDecimal(game.skuCount),
    },
    {
      Key: 'requestActivity',
      Label: t('Request activity'),
      Render: (item: any) => (
        <Sparklines
          data={[
            item.requestActivityScore30to60Days,
            item.requestActivityScore7to30Days,
            item.requestActivityScoreLast7Days,
          ]}
          min={0}
          max={100}
          height={20}
          width={60}
          style={{
            opacity:
              item.requestActivityScore30to60Days +
                item.requestActivityScore7to30Days +
                item.requestActivityScoreLast7Days >
              0
                ? 1
                : 0.5,
          }}
        >
          <SparklinesCurve color="#46b29d" />
        </Sparklines>
      ),
    },
    {
      Key: 'keyActivity',
      Label: t('Key activity'),
      Render: (item: any) => (
        <Sparklines
          data={[
            item.keyActivityScore30to60Days,
            item.keyActivityScore7to30Days,
            item.keyActivityScoreLast7Days,
          ]}
          min={0}
          max={100}
          height={20}
          width={60}
          style={{
            opacity:
              item.keyActivityScore30to60Days +
                item.keyActivityScore7to30Days +
                item.keyActivityScoreLast7Days >
              0
                ? 1
                : 0.5,
          }}
        >
          <SparklinesCurve color="#bb5ebf" />
        </Sparklines>
      ),
    },
    {
      Key: 'appId',
      Label: t('App Id'),
      className: 'autoselect',
      Render: (item: any) =>
        item.appId ? <AutoSelectText>{item.appId}</AutoSelectText> : <NoAppId />,
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} />
        ),
      },
    },
    {
      Key: 'franchise',
      Label: t('Franchise'),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} />
        ),
      },
    },
    {
      Key: 'publisher',
      Label: t('Publisher'),
      Render: (item: any) => (
        <CompanyLink
          id={item.publisherCompanyId}
          name={item.publisherCompanyName}
          imageId={item.publisherCompanyImageId}
          colourHex={item.publisherCompanyColourHex}
        />
      ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} />
        ),
      },
    },
    {
      Key: 'packageId',
      Label: t('Package Id'),
      className: 'autoselect',
      Render: (item: any) =>
        item.packageId ? (
          <AutoSelectText>{item.packageId}</AutoSelectText>
        ) : (
          <NoPackageId />
        ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('Id...')} />
        ),
      },
    },
    {
      Key: 'developer',
      Label: t('Developer'),
      Render: (item: any) => (
        <CompanyLink
          id={item.developerCompanyId}
          name={item.developerCompanyName}
          imageId={item.developerCompanyImageId}
          colourHex={item.developerCompanyColourHex}
        />
      ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} />
        ),
      },
    },
    {
      Key: 'openRequests',
      Label: t('Open requests'),
      className: 'text-right num no-wrap',
      Render: (game: any) => CommaNumber_NoDecimal(game.openRequests),
    },
    {
      Key: 'steamRef',
      Label: t('Product code'),
      className: 'autoselect',
      Render: (item: any) =>
        item.steamRef ? (
          <AutoSelectText>{item.steamRef}</AutoSelectText>
        ) : (
          <NoProductCode />
        ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('Code...')} />
        ),
      },
    },
    {
      Key: 'canRequestFromDate',
      Label: t('Can request from'),
      Render: (item: any) => item.canRequestFromDate,
    },
    {
      Key: 'canRequestUntilDate',
      Label: t('Can request until'),
      Render: (item: any) => item.canRequestUntilDate,
    },
    {
      Key: 'territories',
      Sort: false,
      Label: t('Territories'),
      Render: (item: any) => (
        <div className="restrictions">
          <SkuRestrictions
            isDisallow={item.disallowListedTerritories}
            restrictions={item.territories}
            noDataText={t('No restrictions')}
            render={({ id, name, colour }: any) => (
              <TerritoryLink
                id={id}
                key={id}
                name={name}
                colour={colour}
              ></TerritoryLink>
            )}
          ></SkuRestrictions>
        </div>
      ),
    },
    {
      Key: 'packageType',
      Label: t('Package type'),
      Render: (item: any) => item.packageType,
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('type...')} />
        ),
      },
    },
    {
      Key: 'platform',
      Label: t('Platform'),
      Render: (item: any) => (
        <PlatformLink id={item.platformId} name={item.platform}></PlatformLink>
      ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <SimpleTextFilter Key={col.Key} Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''} onChange={(value: string) => filterChange(col.Key, value)} placeholder={t('platform...')} />
        ),
      },
    },
    {
      Key: 'isArchived',
      Label: t('Status'),
      Render: (game: any) =>
        game.isArchived && game.isLicenseExpired ? (
          <span className="badge bg-danger">
            <FontAwesomeIcon icon={icons.ARCHIVED} className="me-2" />
            {t('Archived')}
          </span>
        ) : game.isArchived ? (
          <span className="badge bg-danger">
            <FontAwesomeIcon icon={icons.ARCHIVED} className="me-2" />
            {t('Archived')}
          </span>
        ) : game.isLicenseExpired ? (
          <span className="badge bg-pink">
            <FontAwesomeIcon icon={icons.GAME_LICENSE_EXPIRED} className="me-2" />
            {t('Expired')}
          </span>
        ) : (
          <span className="badge bg-success">{t('Live')}</span>
        ),
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, el.target.value)}
            >
              <option value="">{t('All')}</option>
              <option value="Live">{t('Live')}</option>
              <option value="Archived">{t('Archived')}</option>
              <option value="Expired">{t('Expired')}</option>
            </select>
          );
        },
      },
    },
    {
      Key: 'creationDate',
      Label: t('Created'),
      Render: (game: any) => <FormattedDate value={game.creationDate} variant="default" />,
    },
    {
      Key: 'buttons-checkbox',
      Label: '',
      Resize: false,
      className: 'text-right no-navigate',
      Sort: false,
      width: '2%',
      Filter: {
        Render: (col: { Key: string; filterValue?: string }) => (
          <Checkbox
            disabled={!games}
            checked={false}
            onChange={(checked: boolean) => handleCheckAllChanged(checked)}
          ></Checkbox>
        ),
      },
      Render: (item: any) =>
        !item.isArchived && (
          <div className="d-flex align-items-center justify-content-end">
            <KebabMenu
              take={1}
              className="inline-icons"
              buttons={[
                userIs(currentUser, userTypes.REQUESTER) &&
                !item.isLicenseExpired &&
                !item.isArchived && (
                  <Button
                    testId={`request-game-key-${item.name}`}
                    className="btn btn-default me-2"
                    onClick={(e) => {
                      onRequestKeys(e, item);
                    }}
                    icon={icons.REQUEST_KEYS}
                    text={t('Request keys')}
                  />
                ),
                userIs(currentUser, userTypes.UPLOADER) &&
                !item.isLicenseExpired &&
                !item.isArchived &&
                (item.skuId || item?.skuCount > 0) && (
                  <Button
                    className="btn btn-default me-2"
                    onClick={(e) => onSteamBulkRequest(e, [item])}
                    icon={icons.REQUEST_ON_STEAM}
                    text={t('Request on Steam')}
                  />
                ),
              ]}
            ></KebabMenu>
            {userIs(currentUser, userTypes.UPLOADER) && (
              <>
                <Checkbox
                  className="ms-3"
                  checked={isChecked(item)}
                  onChange={(checked: boolean) => handleCheckChanged(checked, item)}
                ></Checkbox>
              </>
            )}
          </div>
        ),
    },
  ];
